import { Theme, ThemeOptions } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { hexToCSSFilter } from 'hex-to-css-filter';
import { generateButtonStyles } from '../components/shared/Button/button.utility';
import React from 'react';
import { pxToRem, pixelToRem } from './pxToRem';
import { generateTypographyStyles } from '../components/shared/Typography/typography.utility';
import {
  getTypographyDefaultBody,
  getTypographyDefaultData,
  getTypographyDefaultFormItems,
} from '../components/shared/Typography/typography.data';

// default configuration for hex to css filter
const config = {
  acceptanceLossPercentage: 1,
};

interface NeutralColorPalette {
  main: React.CSSProperties['color'];
}
const generateAllNeutralColor = () => {
  const neutralColors: { [key: string]: React.CSSProperties['color'] } = {
    neutral0: '#FFFFFF',
    neutral1: '#F6F7FB',
    neutral2: '#F2F2F2',
    neutral3: '#E9E9E9',
    neutral4: '#D9D9D9',
    neutral5: '#B5B5B5',
    neutral6: '#999999',
    neutral7: '#595959',
    neutral8: '#444444',
    neutral9: '#000000',
    neutral10: '#b1b1b1',
    neutral11: '#757575',
    neutral12: '#2F2E41',
  };
  const neutralColorsList: { [key: string]: NeutralColorPalette } = {};
  for (const color in neutralColors) {
    neutralColorsList[color] = {
      main: neutralColors[color],
    };
  }
  return neutralColorsList;
};

const defaultHeadingSizeMd = {
  h1: '30px',
  h2: '24px',
  h3: '20px',
  h4: '18px',
  h5: '16px',
};

const defaultHeadingSizeSm = {
  h1: '24px',
  h2: '24px',
  h3: '20px',
  h4: '18px',
  h5: '16px',
};

function getBreakPoints(data: { xl?: number; lg?: number; md?: number; sm?: number; xs?: number }) {
  return {
    xs: data.xs || 0,
    sm: data.sm || 431,
    md: data.md || 769,
    lg: data.lg || 960,
    xl: data.xl || 1300,
  };
}
const DEFAULT_BODY_TEXT_COLOR = '#444444';

// Create theme variables
const generateThemeVariables = (props: any): ThemeOptions => {
  const theme = props?.siteSettings;
  const primaryFontFamilyType =
    theme?.fontFamily?.fields?.primaryFontFamilyType?.value || 'Proxima Nova';
  const primaryFontSemiBoldType =
    theme?.fontFamily?.fields?.primaryFontSemiBoldType?.value || 'Proxima Nova SemiBold';
  const secondaryFontFamilyType =
    theme?.fontFamily?.fields?.secondaryFontFamilyType?.value || 'Proxima Nova';
  const appBarColor = theme?.appBarColor?.value || '#FFFFFF';
  const primaryActionColor = theme?.primaryActionColor?.value || '#015FF1';
  const primaryActionHoverColor = theme?.primaryActionHoverColor?.value || '#0557D5';
  const secondaryMainColor = theme?.secondaryMainColor?.value || '#FFFFFF';
  const secondaryDarkColor = theme?.secondaryDarkColor?.value || '#ECF3FF';
  const textColor = theme?.textColor?.value || DEFAULT_BODY_TEXT_COLOR;
  const themeLightColor = theme?.themeLightColor?.value || '#DFF3F4';
  const themeColor = theme?.themeColor?.value || '#005587'; // Other theme color: Fuschia #C42EA5
  const themeColorHover = theme?.themeColorHover?.value || '#005587';
  const themeColorFilter = `brightness(0) saturate(100%) ${
    hexToCSSFilter(themeColor, config).filter
  }`; // Note that hexToCSSFilter will only work if the image color is black initially hence passing brightness(0) saturate(100%) to make image black
  const themeColorHoverFilter = `brightness(0) saturate(100%) ${
    hexToCSSFilter(themeColorHover, config).filter
  }`;
  const whiteColor = theme?.whiteColor?.value || '#FFFFFF';
  const whiteColorFilter = `brightness(0) saturate(100%) ${
    hexToCSSFilter(whiteColor, config).filter
  }`;

  const headingTextPrimaryColor = theme?.headingFontColor?.value || '#000000';
  const headingTextSecondaryColor = theme?.headingFontColor?.value || '#444444';
  const blackColor = theme?.blackColor?.value || '#000000';
  const hintTextColor = theme?.hintTextColor?.value || '#6E6E6E';
  const backgroundColorType1 = theme?.backgroundColorType1?.value || '#2B3942';
  const backgroundColorType2 = theme?.backgroundColorType2?.value || '#6F6F6F';
  const backgroundColorType3 = theme?.backgroundColorType3?.value || '#F7F8FA';
  const backgroundColorType4 = theme?.backgroundColorType4?.value || '#272727';
  const errorColor = theme?.errorColor?.value || '#E20000';
  const warningColor = theme?.warningColor?.value || '#FF9300';
  const successColor = theme?.successColor?.value || '#009D3F';
  const themeColorSecondary = theme?.themeColorSecondary?.value || '#00beb1';
  const surveyErrorTextColor = theme?.surveyErrorTextColor?.value || '#621B16';
  const surveyErrorBackgroundColor = theme?.surveyErrorBackgroundColor?.value || '#FCE6E6';
  const surveyErrorIconColor = theme?.surveyErrorIconColor?.value || '#F44336';
  const tealColor = theme?.statusColor?.value || '#077D86';
  const violetColor = '#250056';
  const lightBlueColor = '#0076ae';
  const discreateButtonEnableTextColor =
    theme?.discreetButtonTheme?.fields?.enabledTextColor?.value || '#005587';
  const discreateButtonFocusedBorderColor =
    theme?.discreetButtonTheme?.fields?.focusedBorderColor?.value || '#000000';
  const discreateButtonHoverBackgroundColor =
    theme?.discreetButtonTheme?.fields?.hoverBackgroundColor?.value || '#ECF3FF';
  const discreateButtonHoverTextColor =
    theme?.discreetButtonTheme?.fields?.hoverTextColor?.value || '#005587';
  const discreateButtonPressedTextColor =
    theme?.discreetButtonTheme?.fields?.pressedTextColor?.value || '#005587';
  const discreateButtonPressedBackgroundColor =
    theme?.discreetButtonTheme?.fields?.pressedBackgroundColor?.value || '#ECF3FF';
  const discreateButtonBorderRadius =
    theme?.discreetButtonTheme?.fields?.buttonBorderRadius?.value || '50px';
  const discreateButtonHeight = theme?.discreetButtonTheme?.fields?.buttonHeight?.value || '48px';
  const heading = theme?.heading?.value || '#0768FD';
  const pipeColor = theme?.pipeColor?.value || '#7bbcd9';
  const filterTextHeadingColor = theme?.filterTextHeadingColor?.value || '#11557D';
  const backgroundGradientColor1 =
    theme?.backgroundGradientColor1?.value || 'linear-gradient(90deg, #250056 4.5%, #0076AE 100%)';
  const backgroundGradientColor2 =
    theme?.backgroundGradientColor2?.value || 'linear-gradient(1deg, #005587 0%, #15968f 100%)';
  const backgroundGradientColor3 =
    theme?.backgroundGradientColor3?.value ||
    'linear-gradient(283deg, rgba(0, 85, 135, 0.80) -13.55%, rgba(0, 142, 159, 0.80) 8.06%, rgba(0, 191, 179, 0.00) 60.54%)';
  const backgroundGradientColor4 =
    theme?.backgroundGradientColor4?.value ||
    'linear-gradient(1.33deg, #005587 -2.85%, #00BEB1 144.31%)';
  const backgroundGradientColor5 =
    theme?.backgroundGradientColor5?.value ||
    'linear-gradient(73.33deg, #250056 -16.55%, #0076AE 73.37%)';
  const backgroundGradientColor6 =
    theme?.backgroundGradientColor6?.value ||
    'linear-gradient(180deg, rgba(0, 85, 135, 0.8) 3.8%, rgba(0, 142, 159, 0.8) 30.68%, rgba(0, 191, 179, 0) 72.1%)';
  const footerBackgroundColor = theme?.footerBackgroundColor?.value || '#2B3942';
  const fontTextColor1 = theme?.fontTextColor1?.value || '#ffffff'; //Footer text
  const fontTextColor2 = theme?.fontTextColor2?.value || '#444444'; //Home hero banner body text color
  const fontTitleColor1 = theme?.fontTitleColor1?.value || '#1A1A1A'; //Home hero banner title text color

  const maxViewPortSize = theme?.maxViewportSize?.value;

  const breakpoints = getBreakPoints({
    xl: parseInt(maxViewPortSize),
    lg: parseInt(theme?.viewportSizeLg?.value),
  });
  const defaultTypographyData = getTypographyDefaultData({
    primaryColor: headingTextPrimaryColor,
    secondaryColor: headingTextSecondaryColor,
  });
  const defaultTypographyBody = getTypographyDefaultBody({
    primaryColor: theme?.bodyFontColor?.value || DEFAULT_BODY_TEXT_COLOR,
    secondaryColor: theme?.bodyFontColor?.value || '#757575',
  });
  const defaultTypographyFormItems = getTypographyDefaultFormItems({
    primaryColor: generateAllNeutralColor().neutral8.main || blackColor,
  });
  const h1Style = generateTypographyStyles(theme?.h1?.fields, defaultTypographyData.h1);
  const h2Style = generateTypographyStyles(theme?.h2?.fields, defaultTypographyData.h2);
  const h3Style = generateTypographyStyles(theme?.h3?.fields, defaultTypographyData.h3);
  const h4Style = generateTypographyStyles(theme?.h4?.fields, defaultTypographyData.h4);
  const h5Style = generateTypographyStyles(theme?.h5?.fields, defaultTypographyData.h5);
  const h6Style = generateTypographyStyles(theme?.h6?.fields, defaultTypographyData.h6);
  const formLabelStyles = generateTypographyStyles(
    theme?.formLabel?.fields,
    defaultTypographyFormItems.formLabel
  );
  const formValueStyles = generateTypographyStyles(
    theme?.formValue?.fields,
    defaultTypographyFormItems.formValue
  );

  // custom heading size config
  defaultHeadingSizeMd.h1 = h1Style.responsiveFontSize.tablet;
  defaultHeadingSizeSm.h1 = h1Style.responsiveFontSize.mobile;
  defaultHeadingSizeMd.h2 = h2Style.responsiveFontSize.tablet;
  defaultHeadingSizeSm.h2 = h2Style.responsiveFontSize.mobile;
  defaultHeadingSizeMd.h3 = h3Style.responsiveFontSize.tablet;
  defaultHeadingSizeSm.h3 = h3Style.responsiveFontSize.mobile;
  defaultHeadingSizeMd.h4 = h4Style.responsiveFontSize.tablet;
  defaultHeadingSizeSm.h4 = h4Style.responsiveFontSize.mobile;
  defaultHeadingSizeMd.h5 = h5Style.responsiveFontSize.tablet;
  defaultHeadingSizeSm.h5 = h5Style.responsiveFontSize.mobile;

  return {
    palette: {
      primary: {
        main: primaryActionColor,
        dark: primaryActionHoverColor,
      },
      secondary: {
        main: secondaryMainColor,
        dark: secondaryDarkColor,
      },
      text: {
        primary: `${textColor}`,
      },
      white: {
        main: whiteColor,
      },
      black: {
        main: blackColor,
      },
      hintText: {
        main: hintTextColor,
      },
      backgroundType1: {
        main: backgroundColorType1,
      },
      backgroundType2: {
        main: backgroundColorType2,
      },
      backgroundColorType3: {
        main: backgroundColorType3,
      },
      backgroundType4: {
        main: backgroundColorType4,
      },
      error: {
        main: errorColor,
      },
      surveyErrorTextColor: {
        main: surveyErrorTextColor,
      },
      surveyErrorBackgroundColor: {
        main: surveyErrorBackgroundColor,
      },
      surveyErrorIconColor: {
        main: surveyErrorIconColor,
      },
      warning: {
        main: warningColor,
      },
      info: {
        main: warningColor,
      },
      success: {
        main: successColor,
      },
      appBarColor: {
        main: appBarColor,
      },
      themeColor: {
        main: themeColor,
      },
      themeLightColor: {
        main: themeLightColor,
      },
      themeColorSecondary: {
        main: themeColorSecondary,
      },
      themeColorFilter: {
        main: themeColorFilter,
        dark: themeColorHoverFilter,
      },
      whiteColorFilter: {
        main: whiteColorFilter,
      },
      teal: {
        main: tealColor,
      },
      primaryOne: {
        dark: violetColor,
        light: lightBlueColor,
      },
      discreteButton: {
        enabledText: discreateButtonEnableTextColor,
        focusedBorder: discreateButtonFocusedBorderColor,
        hoverBackground: discreateButtonHoverBackgroundColor,
        hoverText: discreateButtonHoverTextColor,
        pressedText: discreateButtonPressedTextColor,
        pressedBackground: discreateButtonPressedBackgroundColor,
        borderRadius: discreateButtonBorderRadius,
        buttonHeight: discreateButtonHeight,
      },
      heading: {
        main: heading,
      },
      pipeColor: {
        main: pipeColor,
      },
      filterTextHeadingColor: {
        main: filterTextHeadingColor,
      },
      backgroundGradientColor1: {
        main: backgroundGradientColor1,
      },
      backgroundGradientColor2: {
        main: backgroundGradientColor2,
      },
      backgroundGradientColor3: {
        main: backgroundGradientColor3,
      },
      backgroundGradientColor4: {
        main: backgroundGradientColor4,
      },
      backgroundGradientColor5: {
        main: backgroundGradientColor5,
      },
      backgroundGradientColor6: {
        main: backgroundGradientColor6,
      },
      footerBackgroundColor: {
        main: footerBackgroundColor,
      },
      fontTextColor1: {
        main: fontTextColor1,
      },
      fontTextColor2: {
        main: fontTextColor2,
      },
      fontTitleColor1: {
        main: fontTitleColor1,
      },
      primaryFontFamilyType: primaryFontFamilyType,
      primaryFontSemiBoldType: primaryFontSemiBoldType,
      secondaryFontFamilyType: secondaryFontFamilyType,
      ...generateAllNeutralColor(),
    },
    typography: {
      fontFamily: `${primaryFontFamilyType}, sans-serif`,
    },
    breakpoints: {
      values: breakpoints,
    },
    components: {
      // Name of the component ⚛️
      MuiButtonBase: {
        defaultProps: {
          // The props to apply
          disableRipple: true, // No more ripple, on the whole application 💣!
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          a: {
            color: primaryActionColor,
          },
          h1: h1Style.isApplicableToRichText ? h1Style.styles : null,
          h2: h2Style.isApplicableToRichText ? h2Style.styles : null,
          h3: h3Style.isApplicableToRichText ? h3Style.styles : null,
          h4: h4Style.isApplicableToRichText ? h4Style.styles : null,
          h5: h5Style.isApplicableToRichText ? h5Style.styles : null,
          h6: h6Style.isApplicableToRichText ? h6Style.styles : null,
        },
      },
      MuiTypography: {
        styleOverrides: {
          body1: generateTypographyStyles(theme?.body?.fields, defaultTypographyBody.body).styles,
          body2: generateTypographyStyles(theme?.body1?.fields, defaultTypographyBody.body1).styles,
          caption: generateTypographyStyles(theme?.caption?.fields, defaultTypographyBody.caption)
            .styles,
          h1: h1Style.styles,
          h2: h2Style.styles,
          h3: h3Style.styles,
          h4: h4Style.styles,
          h5: h5Style.styles,
          h6: h6Style.styles,
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': {
              backgroundColor: generateButtonStyles(theme?.discreetButtonTheme?.fields)?.background
                .hover,
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            ...formLabelStyles.styles,
            '.MuiFormControlLabel-label': formLabelStyles.styles,
            marginLeft: 0,
            '.MuiButtonBase-root': {
              paddingLeft: 0,
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            ...formLabelStyles.styles,
            '&.Mui-focused': {
              color: formLabelStyles.styles.color,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: formValueStyles.styles,
        },
      },
    },
    buttonStyle: {
      primary: generateButtonStyles(theme?.primaryButtonTheme?.fields),
      secondary: generateButtonStyles(theme?.secondaryButtonTheme?.fields),
      discreet: generateButtonStyles(theme?.discreetButtonTheme?.fields),
    },
    bodyColor: theme?.bodyFontColor?.value || DEFAULT_BODY_TEXT_COLOR,
    siteName: `${props?.site?.name}`,
  };
};

// Handle responsive typography
const handleResponsiveTypography = (theme: Theme, props: any): Theme => {
  const primaryFontFamilyType =
    props?.siteSettings?.fontFamily?.fields?.primaryFontFamilyType?.value || 'Proxima Nova';
  const primaryFontSemiBoldType = theme.palette.primaryFontSemiBoldType;

  theme.typography.h1 = {
    ...theme.typography.h1,
    [theme.breakpoints.down('md')]: {
      fontSize: pixelToRem(defaultHeadingSizeMd.h1),
      lineHeight: pixelToRem(defaultHeadingSizeMd.h1),
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: pixelToRem(defaultHeadingSizeSm.h1),
      lineHeight: pixelToRem(defaultHeadingSizeSm.h1),
    },
  };
  theme.typography.h2 = {
    ...theme.typography.h2,
    [theme.breakpoints.down('md')]: {
      fontSize: pixelToRem(defaultHeadingSizeMd.h2),
    },
  };
  theme.typography.h3 = {
    ...theme.typography.h3,
    [theme.breakpoints.down('md')]: {
      fontSize: pixelToRem(defaultHeadingSizeMd.h3),
    },
  };
  theme.typography.h4 = {
    ...theme.typography.h4,
    [theme.breakpoints.down('md')]: {
      fontSize: pixelToRem(defaultHeadingSizeMd.h4),
    },
  };
  theme.typography.h5 = {
    ...theme.typography.h5,
    [theme.breakpoints.down('md')]: {
      fontSize: pixelToRem(defaultHeadingSizeMd.h5),
      lineHeight: pxToRem(24),
    },
  };
  theme.typography.title = {
    fontFamily: `${primaryFontFamilyType}, sans-serif`,
    fontWeight: 600,
    fontSize: pxToRem(32),
    lineHeight: pxToRem(32),
    [theme.breakpoints.up('md')]: {
      fontSize: pxToRem(32),
      lineHeight: pxToRem(32),
      fontWeight: 500,
    },
    [theme.breakpoints.down('md')]: {
      fontSize: pxToRem(24),
      lineHeight: pxToRem(32),
      fontWeight: 500,
    },
    [theme.breakpoints.down(500)]: {
      fontSize: pxToRem(20),
      lineHeight: pxToRem(32),
      fontWeight: 500,
    },
  };
  theme.typography.subtitle1 = {
    fontFamily: `${primaryFontFamilyType}, sans-serif`,
    fontWeight: 400,
    fontSize: pxToRem(22),
    lineHeight: pxToRem(32),
    [theme.breakpoints.down('lg')]: {
      fontSize: pxToRem(16),
      lineHeight: pxToRem(24),
    },
    [theme.breakpoints.up('md')]: {
      fontSize: pxToRem(16),
      lineHeight: pxToRem(24),
    },
    [theme.breakpoints.down('md')]: {
      fontSize: pxToRem(16),
      lineHeight: pxToRem(24),
    },
  };
  theme.typography.body = {
    fontFamily: `"${primaryFontFamilyType} Regular", sans-serif`,
    fontWeight: 400,
    fontSize: pxToRem(16),
    lineHeight: pxToRem(24),
  };
  theme.typography.btnText = {
    fontFamily: `${primaryFontFamilyType}, sans-serif`,
    fontWeight: 600,
    fontSize: pxToRem(16),
    lineHeight: pxToRem(24),
  };
  theme.typography.successCardTitle = {
    fontFamily: `${primaryFontFamilyType}, sans-serif`,
    fontWeight: 600,
    fontSize: pxToRem(18),
    lineHeight: 'normal',
  };
  theme.typography.accordionTitle = {
    fontFamily: `${primaryFontSemiBoldType}`,
    fontWeight: 400,
    fontSize: pxToRem(22),
    lineHeight: pxToRem(30),
    [theme.breakpoints.down('md')]: {
      fontFamily: `${primaryFontFamilyType}, sans-serif`,
      fontWeight: 600,
      fontSize: pxToRem(16),
      lineHeight: pxToRem(24),
    },
  };

  return theme;
};

// Get theme
// Temporarily disable non-any type check to fix eslint warning.
// Will enable it once we get finalized Layoutservice interface
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function getTheme(props: any): Theme {
  const theme = createTheme(generateThemeVariables(props));
  return handleResponsiveTypography(theme, props);
}
